import { compact, differenceBy, flatMap, isEmpty, uniqBy } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  getDocumentsFromRelationships,
  getInheritableDocumentsFromRelationships,
} from '@/modules/documents/documentRelationships.utils';
import { NON_VIEWER_SOURCE_DOCUMENT_TYPES } from '@/modules/documents/documentTypePolicies';
import { DocumentType } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import {
  EntityDocumentViewer_DocumentFragment,
  EntityDocumentViewer_EntityFragment,
  EntityDocumentViewer_TestamentaryEntityFragment,
  EntityDocumentViewerQueryVariables,
  useEntityDocumentViewerQuery,
} from '../graphql/EntityDocumentViewer.generated';
import {
  DocumentViewerTab,
  HouseholdDocumentsViewerContext,
  UpdateActiveDocumentOpts,
} from './HouseholdDocumentsViewer.context';

function useHouseholdDocumentsViewerContextValue({
  targetId = '',
  documentIds,
  showAllHouseholdDocuments = false,
  householdId,
  overrideActiveDocumentId,
  onUpdateActiveDocumentId,
  overrideDocumentViewerActiveTab,
  onUpdateDocumentViewerActiveTab,
  hideAddDocument = false,
  documentOrderFn,
  /** Enables the Dispositive Diagram tab and initializes it active when true */
  showDiagram = false,
}: HouseholdDocumentsViewerProviderProps): HouseholdDocumentsViewerContext {
  const targetKind = targetId ? getPulidKind(targetId) : null;
  if (!targetKind && !documentIds && !showAllHouseholdDocuments) {
    diagnostics.warn(
      'A valid targetId or list of documentIds must be provided to HouseholdDocumentsViewer'
    );
  }

  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(
    // If override is provided, use it; otherwise use first documentId
    overrideActiveDocumentId ?? documentIds?.[0] ?? null
  );
  // Initialize ref to override value to prevent notifying parent about their own override
  const lastNotifiedDocumentIdRef = useRef<string | null>(
    overrideActiveDocumentId ?? null
  );
  const [activeSummaryId, setActiveSummaryId] = useState<string | null>(null);
  const [defaultDocumentId, setDefaultDocumentId] = useState<string | null>(
    null
  );
  const [documentViewerActiveTab, setDocumentViewerActiveTab] =
    useState<DocumentViewerTab>(() => {
      // Only initialize the diagram tab if both the feature flag is enabled AND
      // showDiagram flag is true.
      if (showDiagram) {
        return 'diagram';
      }

      return 'document';
    });
  const [newlyUploadedDocumentId, setNewlyUploadedDocumentId] = useState<
    string | null
  >(null);

  const { data, loading, error, refetch } = useEntityDocumentViewerQuery({
    fetchPolicy: 'cache-and-network',
    variables: getDocumentsQueryVariables(
      targetId,
      householdId,
      targetKind,
      documentIds
    ),
    skip: !targetId && isEmpty(documentIds) && !showAllHouseholdDocuments,
  });

  const {
    targetDocuments,
    otherHouseholdDocuments,
    defaultDocumentId: defaultDocumentIdFromQuery,
    entityKind,
  } = useMemo(() => {
    if (!data) {
      return {
        targetDocuments: null,
        defaultDocumentId: null,
        otherHouseholdDocuments: null,
      };
    }

    const allHouseholdDocuments = getNodes(data.allHouseholdDocuments);

    if (showAllHouseholdDocuments) {
      return {
        // if we're showing literally all household documents, just return all of them in the list of top-level documents
        targetDocuments: allHouseholdDocuments,
        defaultDocumentId: null,
        otherHouseholdDocuments: null,
      };
    }

    // client profile scenario or a list of documentIds were given
    if (data.targetHouseholdDocuments) {
      const otherHouseholdDocuments = differenceBy(
        allHouseholdDocuments,
        getNodes(data.targetHouseholdDocuments),
        'id'
      );

      return {
        targetDocuments: documentOrderFn
          ? documentOrderFn(getNodes(data.targetHouseholdDocuments))
          : getNodes(data.targetHouseholdDocuments),
        otherHouseholdDocuments,
        defaultDocumentId: null,
      };
    }

    if (data.testamentaryEntity) {
      const testamentaryEntity =
        data.testamentaryEntity as EntityDocumentViewer_TestamentaryEntityFragment;

      let documents: EntityDocumentViewer_DocumentFragment[] = [];

      const benefitsFromEntities = compact(
        testamentaryEntity?.benefitsFrom?.map((dp) => dp?.entity)
      );

      const benefitsFromDocuments = flatMap(benefitsFromEntities, (entity) => {
        const rels = getNodes(entity.entityDocumentRelationships);
        return getInheritableDocumentsFromRelationships<EntityDocumentViewer_DocumentFragment>(
          rels,
          entity.kind
        );
      });

      // Get documents from testamentary entity
      const teDocuments =
        getDocumentsFromRelationships<EntityDocumentViewer_DocumentFragment>(
          getNodes(testamentaryEntity?.entityDocumentRelationships)
        );

      // Combine documents from all sources
      documents = uniqBy([...benefitsFromDocuments, ...teDocuments], 'id');

      const otherHouseholdDocuments = differenceBy(
        allHouseholdDocuments,
        documents,
        'id'
      );
      return {
        targetDocuments: documentOrderFn
          ? documentOrderFn(documents)
          : documents,
        defaultDocumentId: null,
        otherHouseholdDocuments,
      };
    }

    if (data.entity) {
      const entity = data.entity as EntityDocumentViewer_EntityFragment;

      const documents: EntityDocumentViewer_DocumentFragment[] =
        getDocumentsFromRelationships<EntityDocumentViewer_DocumentFragment>(
          getNodes(entity.entityDocumentRelationships)
        );

      const otherHouseholdDocuments = differenceBy(
        allHouseholdDocuments,
        documents,
        'id'
      );

      return {
        entityKind: entity.kind,
        targetDocuments: documentOrderFn
          ? documentOrderFn(documents)
          : documents,
        defaultDocumentId: entity.defaultDocument?.id ?? null,
        otherHouseholdDocuments,
      };
    }

    throw new Error('Invalid data returned from EntityDocumentViewerQuery');
  }, [data, documentOrderFn, showAllHouseholdDocuments]);

  // Derive the resolved active document ID based on priority:
  // 1. the active document the user has selected, or if not present
  // 2. the default document as defined by the entity, or if not present
  // 3. the first document in the list, or if not present
  // 4. null (show nothing)
  const resolvedActiveDocumentId = useMemo(
    () =>
      activeDocumentId ??
      defaultDocumentIdFromQuery ??
      targetDocuments?.[0]?.id ??
      null,
    [activeDocumentId, defaultDocumentIdFromQuery, targetDocuments]
  );

  // Initialize state from query data
  useEffect(() => {
    if (showAllHouseholdDocuments) return;

    if (defaultDocumentIdFromQuery) {
      setDefaultDocumentId(defaultDocumentIdFromQuery);
    }

    // Set active document if not already set
    setActiveDocumentId((prev) => {
      if (prev) return prev;
      return defaultDocumentIdFromQuery ?? targetDocuments?.[0]?.id ?? null;
    });
  }, [defaultDocumentIdFromQuery, targetDocuments, showAllHouseholdDocuments]);

  // Notify parent when resolved active document changes
  useEffect(() => {
    if (showAllHouseholdDocuments) return;
    if (!resolvedActiveDocumentId) return;
    if (lastNotifiedDocumentIdRef.current === resolvedActiveDocumentId) return;

    onUpdateActiveDocumentId?.({
      documentId: resolvedActiveDocumentId,
      shouldClearSearchResults: false,
    });
    lastNotifiedDocumentIdRef.current = resolvedActiveDocumentId;
  }, [
    resolvedActiveDocumentId,
    showAllHouseholdDocuments,
    onUpdateActiveDocumentId,
  ]);

  useEffect(() => {
    if (overrideActiveDocumentId) {
      setActiveDocumentId(overrideActiveDocumentId);
      // Mark as notified to prevent callback to parent who initiated this override
      lastNotifiedDocumentIdRef.current = overrideActiveDocumentId;
    }
  }, [overrideActiveDocumentId]);

  useEffect(() => {
    if (overrideDocumentViewerActiveTab) {
      // Respect override only if it's permitted or not diagram when diagram hidden
      if (overrideDocumentViewerActiveTab === 'diagram' && !showDiagram) {
        setDocumentViewerActiveTab('document');
      } else {
        setDocumentViewerActiveTab(overrideDocumentViewerActiveTab);
      }
    }
  }, [overrideDocumentViewerActiveTab, showDiagram]);

  // If the diagram tab is no longer allowed, ensure we are not on it.
  useEffect(() => {
    if (!showDiagram && documentViewerActiveTab === 'diagram') {
      setDocumentViewerActiveTab('document');
    }
  }, [showDiagram, documentViewerActiveTab]);

  // If the diagram tab is now allowed and the feature flag is enabled, switch to it.
  useEffect(() => {
    if (showDiagram) {
      setDocumentViewerActiveTab('diagram');
    }
  }, [showDiagram]);

  const activeDocument = useMemo(() => {
    const allDocuments = [
      ...(targetDocuments ?? []),
      ...(otherHouseholdDocuments ?? []),
    ];

    if (isEmpty(allDocuments)) {
      return null;
    }

    return (
      allDocuments.find((doc) => doc.id === resolvedActiveDocumentId) ?? null
    );
  }, [resolvedActiveDocumentId, targetDocuments, otherHouseholdDocuments]);

  // Reset activeSummaryId when the active document changes
  useEffect(() => {
    if (activeDocument) {
      const summaries = activeDocument.summaries ?? [];
      // Prefer the default summary, then fall back to the first summary
      const defaultSummary = summaries.find(
        (s) => s.generatedFromPrompt?.isDefaultForDocType
      );
      const preferredSummaryId = defaultSummary?.id ?? summaries[0]?.id ?? null;
      // If the current activeSummaryId is not in the new document's summaries, reset to preferred
      const summaryIds = summaries.map((s) => s.id);
      if (activeSummaryId && !summaryIds.includes(activeSummaryId)) {
        setActiveSummaryId(preferredSummaryId);
      } else if (!activeSummaryId) {
        setActiveSummaryId(preferredSummaryId);
      }
    }
    // Note: activeSummaryId is intentionally NOT in the dependency array to prevent
    // re-execution when explicitly updating the summary ID (e.g., via SummarySelector).
    // This effect should only run when the document changes, not when the summary changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocument]);

  const updateDocumentViewerActiveTab = useCallback(
    (tab: DocumentViewerTab) => {
      setDocumentViewerActiveTab(tab);
      onUpdateDocumentViewerActiveTab?.();
    },
    [onUpdateDocumentViewerActiveTab]
  );

  const updateActiveDocumentId = useCallback(
    ({ documentId, shouldClearSearchResults }: UpdateActiveDocumentOpts) => {
      setActiveDocumentId(documentId);
      // Mark as notified to prevent the notification effect from firing again
      lastNotifiedDocumentIdRef.current = documentId;
      onUpdateActiveDocumentId?.({ documentId, shouldClearSearchResults });
    },
    [onUpdateActiveDocumentId]
  );

  const updateActiveSummaryId = useCallback((summaryId: string | null) => {
    setActiveSummaryId(summaryId);
  }, []);

  return {
    householdId,
    targetId,
    targetKind,
    entityKind,

    targetDocuments,
    otherHouseholdDocuments,
    activeDocument,
    activeDocumentId: resolvedActiveDocumentId,
    updateActiveDocumentId,

    activeSummaryId,
    updateActiveSummaryId,

    defaultDocumentId,
    setDefaultDocumentId,

    documentViewerActiveTab,
    updateDocumentViewerActiveTab,

    newlyUploadedDocumentId,
    setNewlyUploadedDocumentId,

    loading,
    error,
    refetch,

    hideAddDocument,
  };
}

export interface HouseholdDocumentsViewerProviderProps {
  /** `targetId` can be an entity ID, testamentary entity ID, or client profile ID */
  targetId?: string;
  /** documentIds should be provided if targetId is not */
  documentIds?: string[];
  /** Use this if there is no underlying ID to target and you want to show all documents for the household */
  showAllHouseholdDocuments?: boolean;

  householdId: string;
  /**
   * This is used to override and set the active document id in the Viewer context
   * by a parent component.
   * Example: when a user clicks to view the evidence of a suggestion.
   */
  overrideActiveDocumentId?: string;
  /**
   * This callback will be called when the active document id is updated via the
   * Viewer context.
   * Example: when a user uses the document selector to view another document.
   */
  onUpdateActiveDocumentId?: ({
    documentId,
    shouldClearSearchResults,
  }: UpdateActiveDocumentOpts) => void;
  /**
   * This is used to change the active tab of the documents viewer side of the split screen.
   * Example: a user is on the Summary tab, but clicks to view the evidence of a suggestion.
   */
  overrideDocumentViewerActiveTab?: DocumentViewerTab;
  /**
   * This callback will be called when the user changes the active tab of
   * the documents viewer side of the split screen.
   */
  onUpdateDocumentViewerActiveTab?: () => void;
  overrideSearchLoading?: boolean;

  /** If true, the "Add new" menu item in the document selector will be hidden */
  hideAddDocument?: boolean;

  /**
   * An optional function to order the documents in the viewer.
   * @param docs
   */
  documentOrderFn?: (
    docs: EntityDocumentViewer_DocumentFragment[]
  ) => EntityDocumentViewer_DocumentFragment[];

  /**
   * Controls visibility/initialization of dispositive diagram tab.
   */
  showDiagram?: boolean;
  /** Primary client id for diagram (optional) */
  initialPrimaryClientId?: string;
}

export const HouseholdDocumentsViewerProvider = ({
  children,
  ...props
}: PropsWithChildren<HouseholdDocumentsViewerProviderProps>) => {
  const value = useHouseholdDocumentsViewerContextValue(props);
  return (
    <HouseholdDocumentsViewerContext.Provider value={value}>
      {children}
    </HouseholdDocumentsViewerContext.Provider>
  );
};

function getDocumentsQueryVariables(
  targetId: string,
  householdId: string,
  targetKind: PulidKind | null,
  documentIds: string[] | undefined
): EntityDocumentViewerQueryVariables {
  const isClientProfile = targetKind === PulidKind.ClientProfile;

  return {
    targetId,
    isEntity: targetKind === PulidKind.Entity,
    isTestamentaryEntity: targetKind === PulidKind.TestamentaryEntity,
    includeTargetDocuments: isClientProfile || !isEmpty(documentIds),
    targetHouseholdDocumentsWhere: {
      hasHouseholdWith: [
        {
          id: householdId,
        },
      ],
      idIn: isEmpty(documentIds) ? undefined : documentIds,
      typeNotIn: NON_VIEWER_SOURCE_DOCUMENT_TYPES,
      typeIn: isClientProfile
        ? [
            DocumentType.Will,
            DocumentType.WillCodicil,
            DocumentType.PowerOfAttorney,
            DocumentType.OperatingAgreement,
            DocumentType.OperatingAgreementAmendment,
          ]
        : undefined,
    },
    allHouseholdDocumentsWhere: {
      typeNotIn: NON_VIEWER_SOURCE_DOCUMENT_TYPES,
      hasHouseholdWith: [
        {
          id: householdId,
        },
      ],
    },
  };
}
